import { track } from '@cartwheel/analytics'
import {
  useClientAppointmentSummaryQuery,
  useClientAppointmentsQuery,
  useCurrentUserQuery,
} from '@cartwheel/api/v1'
import {
  Announcement,
  AnnouncementType,
  type AppointmentSummary,
  AppointmentsSummaryCards,
  DataTable,
  DropdownFilter,
  FilterBar,
  LoadingView,
  Select,
  useToast,
} from '@cartwheel/components'
import { useEffect, useMemo, useRef, useState } from 'react'
import { getAppointmentDisplayStatus } from '../lib/appointment-utils'
import { type Appointment, AppointmentRow } from './appointment-row'
import ClinicalDigest from './clinical-digest'
import ClinicalDigestCard from './clinical-digest-card'

const ROI_EMPTY_STATE_MESSAGE = 'Not available — Release of Information not signed by family'

interface Props {
  clientId: string
  clientTimeZone: string
  userRoles?: string[]
  districtId?: string
  districtName?: string
  showClinicalDigest?: boolean
  showDigestRoiEmptyState?: boolean
  shareableLink?: string | null
}

type SortDirection = 'asc' | 'desc'
type SortKey = 'date' | 'journey' | 'status' | 'appointmentType' | 'clinician'
type TimeFilter = 'all' | 'past' | 'upcoming'

const timeFilterItems = [
  { value: 'all' as const, label: 'All appointments' },
  { value: 'past' as const, label: 'Past sessions only' },
  { value: 'upcoming' as const, label: 'Upcoming sessions only' },
]

function getErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined
  }

  const status = (error as { status?: unknown }).status
  if (typeof status === 'number') {
    return status
  }

  const response = (error as { response?: { status?: unknown } }).response
  if (response && typeof response.status === 'number') {
    return response.status
  }

  return undefined
}

function isUpcoming(appointment: Appointment): boolean {
  const dateStr = appointment.start_time ?? appointment.date
  return new Date(dateStr) >= new Date(new Date().toDateString())
}

export default function ClientAppointmentsList({
  clientId,
  clientTimeZone,
  userRoles,
  districtId,
  districtName,
  showClinicalDigest = false,
  showDigestRoiEmptyState = false,
  shareableLink,
}: Props) {
  const [sortKey, setSortKey] = useState<SortKey>('date')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [selectedServiceLines, setSelectedServiceLines] = useState<string[]>([])
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all')
  const [linkCopied, setLinkCopied] = useState(false)
  const summaryPermissionToastShownRef = useRef(false)
  const hasTrackedTabClick = useRef(false)
  const toast = useToast()

  const handleCopyShareableLink = () => {
    if (!shareableLink) return
    navigator.clipboard.writeText(shareableLink)
    setLinkCopied(true)
    setTimeout(() => setLinkCopied(false), 2000)
  }

  useEffect(() => {
    if (hasTrackedTabClick.current) return
    hasTrackedTabClick.current = true

    track('APPOINTMENTS_TAB_CLICKED', {
      userRoles: userRoles ?? [],
      clientId,
      districtId: districtId ?? null,
      districtName: districtName ?? null,
    })
  }, [userRoles, clientId, districtId, districtName])

  const { data, isLoading, error } = useClientAppointmentsQuery({
    path: { client_id: clientId },
  })
  const { data: summaryData, error: summaryError } = useClientAppointmentSummaryQuery({
    path: { client_id: clientId },
  })
  const { data: currentUser } = useCurrentUserQuery()
  const scheduledCardVariant = currentUser?.feature_flags?.scheduled_appointments_card
    ? 'total-cell'
    : 'future-card'

  const summaryErrorStatus = getErrorStatus(summaryError)
  const allAppointments = data?.appointments || []
  const summary: AppointmentSummary | null = summaryData
    ? {
        completed_count: summaryData.completed_count,
        missed_count: summaryData.missed_count,
        last_appointment_date: summaryData.last_appointment_date ?? null,
        upcoming_appointment_date: summaryData.upcoming_appointment_date ?? null,
        time_in_care: summaryData.time_in_care ?? null,
        care_happened: summaryData.care_happened,
        care_ended: summaryData.care_ended,
        session_limit: summaryData.session_limit ?? null,
        total_scheduled_count: summaryData.total_scheduled_count,
        future_scheduled_count: summaryData.future_scheduled_count,
      }
    : null
  const showSummarySection = summaryErrorStatus !== 403 ? summary : null

  const serviceLineItems = useMemo(() => {
    const names = [...new Set(allAppointments.map((a) => a.journey_name).filter(Boolean))].sort()
    return names.map((name) => ({ value: name!, label: name! }))
  }, [allAppointments])

  const filteredAppointments = useMemo(() => {
    let result = allAppointments

    if (selectedServiceLines.length > 0) {
      result = result.filter(
        (appt) => appt.journey_name && selectedServiceLines.includes(appt.journey_name)
      )
    }

    if (timeFilter === 'past') {
      result = result.filter((appt) => !isUpcoming(appt))
    } else if (timeFilter === 'upcoming') {
      result = result.filter((appt) => isUpcoming(appt))
    }

    const valueForSort = (appointment: Appointment, key: SortKey): number | string => {
      switch (key) {
        case 'date':
          return appointment.start_time
            ? Date.parse(appointment.start_time)
            : Date.parse(appointment.date)
        case 'journey':
          return appointment.journey_name || ''
        case 'status':
          return getAppointmentDisplayStatus(appointment.status, appointment.soft_deleted)
        case 'appointmentType':
          return appointment.appointment_type_name || ''
        case 'clinician':
          return appointment.clinician_name || ''
      }
    }

    const sorted = [...result].sort((left, right) => {
      const leftValue = valueForSort(left, sortKey)
      const rightValue = valueForSort(right, sortKey)

      if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        return leftValue - rightValue
      }

      return String(leftValue).localeCompare(String(rightValue))
    })

    return sortDirection === 'asc' ? sorted : sorted.reverse()
  }, [allAppointments, selectedServiceLines, timeFilter, sortKey, sortDirection])

  useEffect(() => {
    if (summaryErrorStatus !== 403 || summaryPermissionToastShownRef.current) {
      return
    }

    summaryPermissionToastShownRef.current = true
    toast.error({ description: 'You do not have permission to view this section.' })
  }, [summaryErrorStatus, toast])

  const currentClinicianAppointments = filteredAppointments.filter(
    (appointment) => !appointment.from_previous_clinician
  )
  const previousClinicianAppointments = filteredAppointments.filter(
    (appointment) => appointment.from_previous_clinician
  )

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortKey(key)
    setSortDirection('asc')
  }

  const renderAppointmentsTable = (appointments: Appointment[]) => (
    <div className="overflow-x-auto">
      <DataTable.Root className="min-w-full divide-y divide-gray-stroke">
        <DataTable.Header>
          <DataTable.Row>
            <DataTable.HeaderCell
              sortable
              sortDirection={sortKey === 'date' ? sortDirection : null}
              onSort={() => handleSort('date')}
            >
              Date & Time
            </DataTable.HeaderCell>
            <DataTable.HeaderCell
              sortable
              sortDirection={sortKey === 'journey' ? sortDirection : null}
              onSort={() => handleSort('journey')}
            >
              Journey
            </DataTable.HeaderCell>
            <DataTable.HeaderCell
              sortable
              sortDirection={sortKey === 'status' ? sortDirection : null}
              onSort={() => handleSort('status')}
            >
              Status
            </DataTable.HeaderCell>
            <DataTable.HeaderCell
              sortable
              sortDirection={sortKey === 'appointmentType' ? sortDirection : null}
              onSort={() => handleSort('appointmentType')}
            >
              Appointment Type
            </DataTable.HeaderCell>
            <DataTable.HeaderCell
              sortable
              sortDirection={sortKey === 'clinician' ? sortDirection : null}
              onSort={() => handleSort('clinician')}
            >
              Clinician
            </DataTable.HeaderCell>
          </DataTable.Row>
        </DataTable.Header>
        <DataTable.Body className="bg-white divide-y divide-gray-stroke">
          {appointments.map((appointment) => (
            <AppointmentRow
              key={appointment.id}
              appointment={appointment}
              clientTimeZone={clientTimeZone}
            />
          ))}
        </DataTable.Body>
      </DataTable.Root>
    </div>
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full py-20">
        <LoadingView title="Loading appointments..." />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full py-20">
        <div className="text-orange">Error loading appointments</div>
      </div>
    )
  }

  if (allAppointments.length === 0) {
    return (
      <div className="px-4 py-10 lg:px-8 flex-1 overflow-auto">
        {showSummarySection && (
          <AppointmentsSummaryCards
            summary={showSummarySection}
            scheduledCardVariant={scheduledCardVariant}
          />
        )}

        {showClinicalDigest && <ClinicalDigest clientId={clientId} />}
        {showDigestRoiEmptyState && (
          <ClinicalDigestCard
            summaryNotApplicable
            summaryNotApplicableMessage={ROI_EMPTY_STATE_MESSAGE}
          />
        )}

        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="text-charcoal-50 text-lg">No appointments yet</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 py-10 lg:px-8 flex-1 overflow-auto">
      {showSummarySection && (
        <AppointmentsSummaryCards
          summary={showSummarySection}
          scheduledCardVariant={scheduledCardVariant}
        />
      )}

      {showClinicalDigest && <ClinicalDigest clientId={clientId} />}
      {showDigestRoiEmptyState && (
        <ClinicalDigestCard
          summaryNotApplicable
          summaryNotApplicableMessage={ROI_EMPTY_STATE_MESSAGE}
        />
      )}

      <div className="mb-4">
        <h2 className="font-ginto-nord-md text-2xl text-charcoal-75">Appointments</h2>
      </div>

      {shareableLink && (
        <div className="mb-4">
          <Announcement
            type={AnnouncementType.Info}
            title="Client session link"
            message={
              <>
                Share this link with the family for their sessions:{' '}
                <button
                  type="button"
                  onClick={handleCopyShareableLink}
                  className="cursor-pointer underline"
                  title={shareableLink}
                >
                  {linkCopied ? 'Copied!' : shareableLink}
                </button>
              </>
            }
          />
        </div>
      )}

      <FilterBar>
        {serviceLineItems.length > 1 && (
          <DropdownFilter
            label="Service Line"
            items={serviceLineItems}
            onChange={setSelectedServiceLines}
            placeholder="Search service lines..."
            emptyMessage="No service lines found"
            showViewModeToggle={false}
          />
        )}
        <div className="w-52 shrink-0">
          <Select<TimeFilter>
            items={timeFilterItems}
            value={timeFilter}
            onChange={(val) => setTimeFilter(val ?? 'all')}
          />
        </div>
      </FilterBar>

      {filteredAppointments.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="text-charcoal-50 text-lg">
              No appointments match the selected filters
            </div>
          </div>
        </div>
      ) : (
        <>
          {currentClinicianAppointments.length > 0 &&
            renderAppointmentsTable(currentClinicianAppointments)}

          {previousClinicianAppointments.length > 0 && (
            <div className="mt-10">
              <h3 className="mb-4 font-ginto-nord-md text-lg text-charcoal-75">
                Previous Clinician
              </h3>
              {renderAppointmentsTable(previousClinicianAppointments)}
            </div>
          )}
        </>
      )}
    </div>
  )
}
